/**
 * Nigeria Map SVG Component
 * Renders the full Nigeria map or individual state silhouettes
 */

import { useEffect, useRef, useMemo, forwardRef } from 'react';
import { gsap } from 'gsap';
import type { StateGeoFeature } from '../../types';
import { 
  generateNigeriaMapPaths, 
  generateStateSvgPath,
  OIL_STATES,
  NORTHERN_STATES,
  SOUTHERN_STATES 
} from '../../utils/statePathGenerator';

interface NigeriaMapProps {
  features: StateGeoFeature[];
  width?: number;
  height?: number;
  highlightedStates?: string[];
  highlightColor?: string;
  defaultColor?: string;
  className?: string;
  animate?: boolean;
  showLabels?: boolean;
  regionHighlight?: 'north' | 'south' | 'oil' | null;
  onStateClick?: (stateName: string) => void;
}

export const NigeriaMap = forwardRef<SVGSVGElement, NigeriaMapProps>(({
  features,
  width = 400,
  height = 400,
  highlightedStates = [],
  highlightColor = '#06b6d4',
  defaultColor = '#334155',
  className = '',
  animate = false,
  showLabels = false,
  regionHighlight = null,
  onStateClick,
}, ref) => {
  const pathsRef = useRef<Map<string, SVGPathElement>>(new Map());
  
  const paths = useMemo(() => 
    generateNigeriaMapPaths(features, width, height), 
    [features, width, height]
  );
  
  // Determine if a state should be highlighted based on region
  const isHighlighted = (stateName: string) => {
    if (highlightedStates.includes(stateName)) return true;
    
    if (regionHighlight === 'north') return NORTHERN_STATES.includes(stateName);
    if (regionHighlight === 'south') return SOUTHERN_STATES.includes(stateName);
    if (regionHighlight === 'oil') return OIL_STATES.includes(stateName);
    
    return false;
  };
  
  // Animation effect
  useEffect(() => {
    if (!animate) return;
    
    const pathElements = Array.from(pathsRef.current.values());
    
    // Reset and animate
    gsap.set(pathElements, { opacity: 0, scale: 0.8, transformOrigin: 'center' });
    gsap.to(pathElements, {
      opacity: 1,
      scale: 1,
      duration: 0.8,
      stagger: {
        each: 0.02,
        from: 'random',
      },
      ease: 'back.out(1.7)',
    });
  }, [animate]);
  
  return (
    <svg
      ref={ref}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
    >
      <g className="nigeria-map">
        {Array.from(paths.entries()).map(([stateName, pathD]) => {
          const highlighted = isHighlighted(stateName);
          
          return (
            <path
              key={stateName}
              ref={el => {
                if (el) pathsRef.current.set(stateName, el);
              }}
              d={pathD}
              fill={highlighted ? highlightColor : defaultColor}
              stroke="#0f172a"
              strokeWidth="0.5"
              className={`
                transition-colors duration-300
                ${onStateClick ? 'cursor-pointer hover:brightness-125' : ''}
              `}
              onClick={() => onStateClick?.(stateName)}
              data-state={stateName}
            >
              {showLabels && <title>{stateName}</title>}
            </path>
          );
        })}
      </g>
    </svg>
  );
});

NigeriaMap.displayName = 'NigeriaMap';

interface StateSilhouetteProps {
  feature: StateGeoFeature;
  size?: number;
  color?: string;
  className?: string;
  animate?: boolean;
  glowColor?: string;
}

export const StateSilhouette = forwardRef<SVGSVGElement, StateSilhouetteProps>(({
  feature,
  size = 100,
  color = '#06b6d4',
  className = '',
  animate = false,
  glowColor,
}, ref) => {
  const pathRef = useRef<SVGPathElement>(null);
  
  const pathD = useMemo(() => 
    generateStateSvgPath(feature, size, 10),
    [feature, size]
  );
  
  useEffect(() => {
    if (!animate || !pathRef.current) return;
    
    const path = pathRef.current;
    const length = path.getTotalLength();
    
    // Draw animation
    gsap.set(path, {
      strokeDasharray: length,
      strokeDashoffset: length,
      fill: 'transparent',
    });
    
    gsap.to(path, {
      strokeDashoffset: 0,
      duration: 1.5,
      ease: 'power2.inOut',
      onComplete: () => {
        gsap.to(path, {
          fill: color,
          duration: 0.5,
        });
      },
    });
  }, [animate, color]);
  
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
    >
      {glowColor && (
        <defs>
          <filter id={`glow-${feature.properties.admin1Name}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}
      <path
        ref={pathRef}
        d={pathD}
        fill={color}
        stroke={color}
        strokeWidth="2"
        filter={glowColor ? `url(#glow-${feature.properties.admin1Name})` : undefined}
      />
    </svg>
  );
});

StateSilhouette.displayName = 'StateSilhouette';

export default NigeriaMap;

